import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getCurrentEvent,
  getCurrentEventStatus,
  getNextNEvents,
} from "core-integration/src/store/events/reducers";
import { getSettings } from "core-integration/src/store/game/reducers";
import type { EntryCrestData } from "core-integration/src/store/ui";
import { Alert } from "plos/src/components/alerts";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import PageTitle from "plos/src/components/PageTitle";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import {
  contentMain,
  leftSidebarLayout,
  statusMain,
  statusSidebar,
} from "plos/src/layouts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAdobeContext } from "../../contexts/AdobeContext";
import AdminPanel from "../EntryInfo/AdminPanel";
import CrestPanel from "../EntryInfo/CrestPanel";
import { useEntryInfo } from "../EntryInfo/hooks/useEntryInfo";
import PointsAndRankings from "../EntryInfo/PointsAndRankings";
import TransfersInfoPanel from "../EntryInfo/TransfersInfoPanel";
import { LeaderboardAd } from "../LeaderboardAd";
import { NewsPlaylist } from "../news/NewsPlaylist";
import { NewsSection } from "../news/NewsSection";
import EventStatus from "../status/EventStatus";
import { ContextualHelpSheet } from "../Tutorials";
import StatusHelp from "../Tutorials/HelpPages/StatusHelp";
import { UpcomingDeadlines } from "../UpcomingDeadlines";
import EventInfo from "./EventInfo";
import { EventSummary } from "./EventSummary";
import { BestLeagues, ValuableTeams } from "./LeaguesAndTeams";
import { DreamTeamPanel, PlayerAvailability } from "./PlayerTables";
import { statusTwoCol } from "./status.css";
import { TopElementCarousel } from "./TopElementCarousel";
import TransfersTable from "./TransfersTable";
// import WscStoriesWidget from "../wsc/WscStoriesWidget";
import { RootState } from "core-integration/src/store";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { useTrackingContext } from "../../contexts/TrackingContext";
import type { StatusContentProps } from "./types";

const StatusContent = ({ entry, now }: StatusContentProps) => {
  const { t } = useTranslation("status");
  const { fireClickTrackEvent } = useTrackingContext();

  const trackOpenHelp = () => {
    fireClickTrackEvent({
      event_component: "button click",
      event_category: "fantasy gameweek status",
      event_detail: "Open Help",
    });
  };

  const settings = useSelector(getSettings);
  const statusData = useSelector(getCurrentEventStatus);
  const nextEvents = useSelector(getNextNEvents(5));
  const { mine, transfersState } = useEntryInfo(entry.id);

  const { getTemplate } = useAdobeContext();

  const [adobeGameweekTemplateUrl, setAdobeGameweekTemplateUrl] = useState<
    string | undefined
  >();
  const [adobeSeasonTemplateUrl, setAdobeSeasonTemplateUrl] = useState<
    string | undefined
  >();

  useEffect(() => {
    (async () => {
      if (settings?.static_content_url) {
        const [gameweekTemplateUrl, seasonTemplateUrl] = await Promise.all([
          getTemplate({
            baseUrl: settings?.static_content_url,
            entry,
            event: now,
            type: "gameweek",
          }),
          getTemplate({
            baseUrl: settings?.static_content_url,
            entry,
            event: now,
            type: "season",
          }),
        ]);

        setAdobeGameweekTemplateUrl(gameweekTemplateUrl);
        setAdobeSeasonTemplateUrl(seasonTemplateUrl);
      }
    })();
  }, [getTemplate, settings, entry, now]);

  // Pass only the crest-related subset so CrestPanel stays decoupled from full IEntry.
  const entryCrestData: EntryCrestData = {
    id: entry.id,
    name: entry.name,
    club_badge_src: entry.club_badge_src,
  };

  return (
    <>
      <LeaderboardAd slot="Leaderboard_Status" id="ism-status-ad" />
      <ContextualHelpSheet
        onOpen={trackOpenHelp}
        title={t("contextualHelp.status.title", {
          ns: "help",
          defaultValue: "The Status page explained",
        })}
      >
        <StatusHelp />
      </ContextualHelpSheet>
      <div className={leftSidebarLayout}>
        <div className={statusMain}>
          <SurfaceContainer>
            <div className={contentMain}>
              <PageTitle
                title={t("eventStatusTitle", "{{eventName}} Status", {
                  eventName: now.name,
                })}
              />
              {statusData?.leagues === "Updated" && (
                <Alert isContentCentered variant="light">
                  {t("leagueTablesUpdated", "League tables have been updated!")}
                </Alert>
              )}
              {statusData?.leagues === "Updating" && (
                <Alert isContentCentered variant="light">
                  {t(
                    "leagueTablesUpdating",
                    "League tables and autosubs are updating...",
                  )}
                </Alert>
              )}
              <div className={statusTwoCol}>
                <EventStatus />
                <EventInfo now={now} />
              </div>
            </div>
          </SurfaceContainer>
          <div className={statusTwoCol}>
            <TransfersTable />
            <TransfersTable isOut={true} />
          </div>
          {/* TODO: Comment back in when we know how to handle no response data. See https://github.com/ismfg/games/pull/8240#issuecomment-3089258727 */}
          {/* <StatusPanel title="Latest Videos">
            <WscStoriesWidget labels="fpl_stories" />
          </StatusPanel> */}
          <TopElementCarousel now={now} />

          <div className={statusTwoCol}>
            <DreamTeamPanel now={now} />
            <PlayerAvailability />
          </div>
          <div className={statusTwoCol}>
            <ValuableTeams />
            <BestLeagues />
          </div>

          <NewsSection title={t("latestScout", "Latest from The Scout")!}>
            <NewsPlaylist
              layout="scrollableRow"
              playlistId={4349948}
              variant="scrollable"
            />
          </NewsSection>
        </div>
        <div className={statusSidebar}>
          <EventSummary
            entry={entry}
            currentEvent={now}
            adobeGameweekTemplateUrl={adobeGameweekTemplateUrl}
            adobeSeasonTemplateUrl={adobeSeasonTemplateUrl}
          />

          <SurfaceContainer>
            <UpcomingDeadlines events={nextEvents} />
          </SurfaceContainer>
          <SurfaceContainer>
            <div className={contentMain}>
              <PointsAndRankings entry={entry} />
              <HorizontalDivider />
              <CrestPanel entryCrestData={entryCrestData} />
              <HorizontalDivider />
              <TransfersInfoPanel
                entry={entry}
                transfersState={transfersState}
                mine={mine}
              />
              {mine && (
                <>
                  <HorizontalDivider />
                  <AdminPanel />
                </>
              )}
            </div>
          </SurfaceContainer>
        </div>
      </div>
    </>
  );
};

const Status = () => {
  const player = useSelector(getPlayerData);
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );
  const now = useSelector(getCurrentEvent);
  const { firePageViewEvent } = useTrackingContext();

  useEffect(() => {
    firePageViewEvent("fantasy status");
  }, []);

  if (!now || !entry) {
    return null;
  }

  return <StatusContent entry={entry} now={now} />;
};

export default Status;
